import React, { useEffect, useRef, useState } from "react"

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

// Animates between numeric values with rAF + easeOutCubic.
// First paint: ramps from 0 → value over `introMs` (default 1500ms).
// Subsequent prop changes: tweens over `tweenMs` (default 800ms).
// `format` shapes the displayed string (defaults to identity).
// `armed` (default true) gates the very first ramp — pass false until
// the element scrolls into view so the intro tween fires there instead
// of silently completing while the tile is off-screen.
export default function TickingNumber({ value, format = (n) => String(n), introMs = 1500, tweenMs = 800, armed = true }) {
  const [display, setDisplay] = useState(0)
  const lastValueRef = useRef(0)
  const rafRef = useRef(null)
  const firstRunRef = useRef(true)

  useEffect(() => {
    if (!armed) return
    if (typeof value !== "number" || Number.isNaN(value)) return

    cancelAnimationFrame(rafRef.current)

    const from = firstRunRef.current ? 0 : lastValueRef.current
    const to = value
    const duration = firstRunRef.current ? introMs : tweenMs
    firstRunRef.current = false

    if (from === to) {
      setDisplay(to)
      lastValueRef.current = to
      return
    }

    const startTime = performance.now()

    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration)
      const eased = easeOutCubic(t)
      const current = from + (to - from) * eased
      setDisplay(current)

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        setDisplay(to)
        lastValueRef.current = to
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [value, introMs, tweenMs, armed])

  return <span style={{ fontVariantNumeric: "tabular-nums" }}>{format(display)}</span>
}
